import { BottomSheet, Box } from '@deckee/deck-hand';
import React, { FC, ReactNode, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { ROUTE_MAP } from '../../../services/route/route-names';
import { transitionToNextRoute } from '../../../services/route/route-navigation';
import {
  setHideStatusBar,
  setShowingMenuBar,
  setShowMapOverlay,
  setTransparentStatusBar,
} from '../../../services/store/modules/app-controls';
import PageHolderContent from '../../containers/PageHolderContent/PageHolderContent';
import { close } from '../../shared/vector-images';
import HeaderBar from '../components/HeaderBar/HeaderBar';
import HeaderBarButton from '../components/HeaderBarButton/HeaderBarButton';
import { LayoutBody } from '../components/LayoutBody/LayoutBody';

interface SheetLayoutProps {
  showMenuBar?: boolean;
  popover?: boolean;
  maxViewHeight?: string;
  minViewHeight?: string;
  showMapOverlay?: boolean;
  heading: React.ReactNode | string;
  renderHeaderBar?: (
    heading: React.ReactNode | string,
    headingPosition?: 'left' | 'center',
  ) => ReactNode;
  headingPosition?: 'left' | 'center';
  sheetRef?: React.RefObject<HTMLDivElement>;
  onClose?: () => unknown;
}

const SheetLayout: FC<SheetLayoutProps> = ({
  showMenuBar = true,
  showMapOverlay = false,
  heading,
  children,
  headingPosition,
  renderHeaderBar,
  maxViewHeight,
  minViewHeight,
  sheetRef,
  popover,
  onClose,
}) => {
  const dispatch = useDispatch();
  const hideStatusBar = !showMapOverlay;

  const defaultClose = () => transitionToNextRoute(ROUTE_MAP);
  const handleClose = onClose || defaultClose;

  useEffect(() => {
    dispatch({
      type: 'SHEET_OPEN',
    });
    if (popover) {
      dispatch({
        type: 'FORCE_POPOVER',
      });
    }
    dispatch(setShowingMenuBar(showMenuBar));
    dispatch(setShowMapOverlay(showMapOverlay));
    dispatch(setTransparentStatusBar(true));
    dispatch(setHideStatusBar(hideStatusBar));

    return () => {
      dispatch(setHideStatusBar(false));
      dispatch(setTransparentStatusBar(false));

      if (popover) {
        dispatch({
          type: 'POPOVER_CLOSE',
        });
      }
    };
  }, [dispatch, showMapOverlay, showMenuBar, hideStatusBar, popover]);

  const DefaultHeader = () => (
    <HeaderBar
      leftNode={headingPosition === 'left' && heading}
      centerNode={headingPosition === 'center' && heading}
      rightNode={<HeaderBarButton icon={close()} onClick={handleClose} />}
      popover={popover}
    />
  );

  return (
    <PageHolderContent transparentBackground>
      <BottomSheet padding="0" overflow="hidden">
        {/* TODO: Make the HeaderBar the BottomSheet.Header? Could be done when `newygreg` moves the bottom sheet out of deckhand */}
        {renderHeaderBar ? (
          renderHeaderBar(heading, headingPosition)
        ) : (
          <DefaultHeader />
        )}

        <Box
          ref={sheetRef}
          minHeight={minViewHeight && `${minViewHeight}vh`}
          maxHeight={`${maxViewHeight}vh`}
          overflow="auto"
        >
          {children}
        </Box>
      </BottomSheet>
    </PageHolderContent>
  );
};

export const SheetBody = LayoutBody;

SheetLayout.defaultProps = {
  showMapOverlay: undefined,
  showMenuBar: undefined,
  headingPosition: 'center',
  renderHeaderBar: undefined,
  maxViewHeight: '77',
  minViewHeight: undefined,
  sheetRef: undefined,
  onClose: undefined,
  popover: undefined,
};

export default SheetLayout;
